"use client";
import { CookieSettings } from "@/components/CookieBanner";
import {
	faCookie,
	faCookieBite,
	faGlobe,
	faList,
	faShieldHalved,
	faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { useState } from "react";

const CookiesPage = () => {
	const [activeSection, setActiveSection] = useState<string | null>(null);

	const cookiesSections = [
		{
			id: "que-son",
			title: "¿Qué son las cookies?",
			icon: faCookie,
			content: (
				<>
					<p className="mb-4">
						En inglés, el término "cookie" significa galleta, pero en el ámbito de la navegación web, una
						"cookie" es algo completamente distinto. Cuando accede a nuestro Sitio Web, en el navegador de su
						dispositivo se almacena una pequeña cantidad de texto que se denomina "cookie". Este texto
						contiene información variada sobre su navegación, hábitos, preferencias, personalizaciones de
						contenidos, etc...
					</p>
					<p className="mb-4">
						Existen otras tecnologías que funcionan de manera similar y que también se usan para recopilar
						datos sobre tu actividad de navegación. Llamaremos "cookies" a todas estas tecnologías en su
						conjunto.
					</p>
					<p>Los usos concretos que hacemos de estas tecnologías se describen en el presente documento.</p>
				</>
			),
		},
		{
			id: "para-que",
			title: "¿Para qué se utilizan las cookies en esta web?",
			icon: faCookieBite,
			content: (
				<>
					<p className="mb-4">
						Las cookies son una parte esencial de cómo funciona el Sitio Web. El objetivo principal de
						nuestras cookies es mejorar su experiencia en la navegación. Por ejemplo, para recordar sus
						preferencias (idioma, país, etc.) durante la navegación y en futuras visitas. La información
						recogida en las cookies nos permite además mejorar la web, adaptarla a sus intereses como usuario,
						acelerar las búsquedas que realice, etc..
					</p>
					<p>
						En determinados casos, si hemos obtenido su previo consentimiento informado, podremos utilizar
						cookies para otros usos, como por ejemplo para obtener información que nos permita mostrarle
						publicidad basada en el análisis de sus hábitos de navegación.
					</p>
				</>
			),
		},
		{
			id: "para-que-no",
			title: "¿Para qué NO se utilizan las cookies en esta web?",
			icon: faShieldHalved,
			content: (
				<p>
					En las cookies que utilizamos no se almacena información sensible de identificación personal como su
					nombre, dirección, tu contraseña, etc...
				</p>
			),
		},
		{
			id: "quien-usa",
			title: "¿Quién utiliza la información almacenada en las cookies?",
			icon: faGlobe,
			content: (
				<>
					<p className="mb-4">
						La información almacenada en las cookies de nuestro Sitio Web es utilizada exclusivamente por
						nosotros, a excepción de aquellas identificadas más adelante como "cookie de terceros", que son
						utilizadas y gestionadas por entidades externas que nos proporcionan servicios que mejoran la
						experiencia del usuario. Por ejemplo las estadísticas que se recogen sobre el número de visitas,
						el contenido que más gusta, etc... lo suele gestionar Google Analytics.
					</p>
					<p>
						Las cookies de terceros también se suelen utilizar para garantizar las operaciones de pago
						realizadas a través del Sitio Web.
					</p>
				</>
			),
		},
		{
			id: "evitar",
			title: "¿Cómo puede evitar el uso de cookies en este Sitio Web?",
			icon: faShieldHalved,
			content: (
				<>
					<p className="mb-4">
						Si prefiere evitar el uso de las cookies, puede RECHAZAR su uso o puede CONFIGURAR las que quiere
						evitar y las que permite utilizar (en este documento le damos información ampliada al respecto de
						cada tipo de cookie, su finalidad, destinatario, temporalidad, etc... ).
					</p>
					<p>
						Si las ha aceptado, no volveremos a preguntarle a menos que borre las cookies en su dispositivo
						según se indica en el apartado siguiente. Si quiere revocar el consentimiento tendrá que eliminar
						las cookies y volver a configurarlas.
					</p>
				</>
			),
		},
		{
			id: "deshabilitar",
			title: "¿Cómo deshabilito y elimino la utilización de cookies?",
			icon: faSliders,
			content: (
				<>
					<p className="mb-4">
						El Titular muestra información sobre su Política de cookies en el banner de cookies accesible en
						todas las páginas del Sitio Web. El banner de cookies muestra información esencial sobre el
						tratamiento de datos y permite al Usuario realizar las siguientes acciones:
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li>
							ACEPTAR o RECHAZAR la instalación de cookies, o retirar el consentimiento previamente otorgado.
						</li>
						<li>
							Obtener información adicional en la página de{" "}
							<a href="/politica-de-cookies/" className="text-red-400 hover:text-red-300">
								Política de Cookies
							</a>
							.
						</li>
					</ul>
					<p className="mb-4">
						Para restringir, bloquear o borrar las cookies de este Sitio Web (y las usada por terceros) puede
						hacerlo, en cualquier momento, modificando la configuración de su navegador. Tenga en cuenta que
						esta configuración es diferente en cada navegador.
					</p>
					<p className="mb-4">
						En los siguientes enlaces encontrará instrucciones para habilitar o deshabilitar las cookies en
						los navegadores más comunes.
					</p>
					<ul className="list-disc pl-6 space-y-2">
						<li>
							<a
								href="http://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-que-los-sitios-we"
								className="text-red-400 hover:text-red-300"
								target="_blank"
								rel="nofollow"
							>
								Firefox
							</a>
						</li>
						<li>
							<a
								href="https://support.google.com/chrome/answer/95647?hl=es"
								className="text-red-400 hover:text-red-300"
								target="_blank"
								rel="nofollow"
							>
								Google Chrome
							</a>
						</li>
						<li>
							<a
								href="https://support.microsoft.com/es-es/help/17442/windows-internet-explorer-delete-manage-cookies#ie=&quot;ie-10"
								className="text-red-400 hover:text-red-300"
								target="_blank"
								rel="nofollow"
							>
								Internet Explorer
							</a>
						</li>
						<li>
							<a
								href="https://support.microsoft.com/es-es/help/4468242/microsoft-edge-browsing-data-and-privacy"
								className="text-red-400 hover:text-red-300"
								target="_blank"
								rel="nofollow"
							>
								Microsoft Edge
							</a>
						</li>
						<li>
							<a
								href="http://support.apple.com/kb/HT1677?viewlocale=es_ES"
								className="text-red-400 hover:text-red-300"
								target="_blank"
								rel="nofollow"
							>
								Safari
							</a>
						</li>
					</ul>
				</>
			),
		},
		{
			id: "tipos",
			title: "¿Qué tipos de cookies se utilizan en esta página web?",
			icon: faList,
			content: (
				<>
					<p className="mb-4">
						Cada página web utiliza sus propias cookies. En nuestra web utilizamos las que se indican a
						continuación:
					</p>
					<p className="mb-4 font-bold">SEGÚN LA ENTIDAD QUE LO GESTIONA</p>
					<p className="mb-4 font-bold">Cookies propias:</p>
					<p className="mb-4">
						Son aquellas que se envían al equipo terminal del Usuario desde un equipo o dominio gestionado por
						el propio editor y desde el que se presta el servicio solicitado por el Usuario.
					</p>
					<p className="mb-4 font-bold">Cookies de terceros:</p>
					<p className="mb-4">
						Son aquellas que se envían al equipo terminal del Usuario desde un equipo o dominio que no es
						gestionado por el editor, sino por otra entidad que trata los datos obtenidos través de las
						cookies.
					</p>
					<p className="mb-4">
						En el caso de que las cookies sean servidas desde un equipo o dominio gestionado por el propio
						editor, pero la información que se recoja mediante estas sea gestionada por un tercero, no pueden
						ser consideradas como cookies propias si el tercero las utiliza para sus propias finalidades (por
						ejemplo, la mejora de los servicios que presta o la prestación de servicios de carácter
						publicitario a favor de otras entidades).
					</p>
					<p className="mb-4 font-bold">SEGÚN SU FINALIDAD</p>
					<p className="mb-4 font-bold">Cookies técnicas:</p>
					<p className="mb-4">
						Son aquellas necesarias para la navegación y el buen funcionamiento de nuestro Sitio Web, como por
						ejemplo, controlar el tráfico y la comunicación de datos, identificar la sesión, acceder a partes
						de acceso restringido,recordar los elementos que integran un pedido, realizar el proceso de compra
						de un pedido, gestionar el pago, controlar el fraude vinculado a la seguridad del servicio,
						realizar la solicitud de inscripción o participación en un evento, contar visitas a efectos de la
						facturación de licencias del software con el que funciona el servicio del Sitio Web, utilizar
						elementos de seguridad durante la navegación, almacenar contenidos para la difusión de vídeos o
						sonido, habilitar contenidos dinámicos (por ejemplo, animación de carga de un texto o imagen) o
						compartir contenidos a través de redes sociales.
					</p>
					<p className="mb-4 font-bold">Cookies de análisis:</p>
					<p className="mb-4">
						Permiten cuantificar el número de usuarios y así realizar la medición y análisis estadístico de la
						utilización que hacen los usuarios del Sitio Web. Para ello se analiza su navegación en nuestro
						Sitio Web con el fin de mejorar la oferta de productos o servicios que le ofrecemos.
					</p>
					<p className="mb-4 font-bold">Cookies de preferencias o personalización:</p>
					<p className="mb-4">
						Son aquellas que permiten recordar información para que el Usuario acceda al servicio con
						determinadas características que pueden diferenciar su experiencia de la de otros usuarios, como,
						por ejemplo, el idioma, el número de resultados a mostrar cuando el Usuario realiza una búsqueda,
						el aspecto o contenido del servicio en función del tipo de navegador a través del cual el Usuario
						accede al servicio o de la región desde la que accede al servicio, etc.
					</p>
					<p className="mb-4 font-bold">SEGÚN EL PLAZO DE TIEMPO QUE PERMANECEN ACTIVADAS</p>
					<p className="mb-4 font-bold">Cookies de sesión:</p>
					<p className="mb-4">
						Son aquellas diseñadas para recabar y almacenar datos mientras el Usuario accede a una página web.
					</p>
					<p className="mb-4">
						Se suelen emplear para almacenar información que solo interesa conservar para la prestación del
						servicio solicitado por el Usuario en una sola ocasión (por ejemplo, una lista de productos
						adquiridos) y desaparecen al terminar la sesión.
					</p>
					<p className="mb-4 font-bold">Cookies persistentes:</p>
					<p className="mb-4">
						Son aquellas en las que los datos siguen almacenados en el terminal y pueden ser accedidos y
						tratados durante un periodo definido por el responsable de la cookie, y que puede ir de unos
						minutos a varios años. A este respecto debe valorarse específicamente si es necesaria la
						utilización de cookies persistentes, puesto que los riesgos para la privacidad podrían reducirse
						mediante la utilización de cookies de sesión. En todo caso, cuando se instalen cookies
						persistentes, se recomienda reducir al mínimo necesario su duración temporal atendiendo a la
						finalidad de su uso. A estos efectos, el Dictamen 4/2012 del GT29 indicó que para que una cookie
						pueda estar exenta del deber de consentimiento informado, su caducidad debe estar relacionada con
						su finalidad. Debido a ello, es mucho más probable que se consideren como exceptuadas las cookies
						de sesión que las persistentes.
					</p>
					<p className="mb-4 font-bold">Detalle de cookies utilizadas en esta web:</p>
					<div className="bg-gray-800 p-4 rounded-md">
						<CookieSettings />
					</div>
				</>
			),
		},
	];

	const scrollToSection = (id: string) => {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveSection(id);
		}
	};

	return (
		<main className="bg-black text-white">
			{/* Hero Section */}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/collections/Precios/custom.webp')" }}
			>
				<div className="absolute inset-0 bg-black/60"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">
							Política de Cookies
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-12"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="">Uso de cookies en Los Amos del Calabozo</strong>
					</motion.p>
				</div>
			</section>

			{/* Quick Navigation */}
			<section className="py-8 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<div className="flex flex-wrap justify-center gap-4">
						{cookiesSections.map((section) => (
							<button
								key={section.id}
								onClick={() => scrollToSection(section.id)}
								className={`px-4 py-2 rounded-md transition-all ${
									activeSection === section.id
										? "bg-red-700 text-white"
										: "bg-gray-800 text-gray-300 hover:bg-gray-700"
								}`}
							>
								<FontAwesomeIcon icon={section.icon} className="mr-2" />
								{section.title}
							</button>
						))}
					</div>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					{cookiesSections.map((section) => (
						<motion.div
							key={section.id}
							id={section.id}
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6 }}
							className="mb-16"
						>
							<h2 className="text-3xl font-amarante headerFont mb-8 border-b-2 border-red-700 pb-2 inline-block">
								<FontAwesomeIcon icon={section.icon} className="mr-3 text-red-500" />
								{section.title}
							</h2>

							<div
								className={`bg-gray-900 rounded-lg p-6 border-l-4 transition-all duration-300 ${
									activeSection === section.id
										? "border-red-500 shadow-lg shadow-red-900/20"
										: "border-red-700"
								}`}
							>
								<div className="text-gray-300">{section.content}</div>
							</div>
						</motion.div>
					))}
				</div>
			</section>
		</main>
	);
};

export default CookiesPage;
